import React, { useEffect, useState } from 'react';
import { Newspaper, ExternalLink, Award, Play, Mail, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Press = () => {
  const [ytVideos, setYtVideos] = useState([]);
  const [ytLoading, setYtLoading] = useState(true);
  const [ytError, setYtError] = useState(false);

  // Subscribe form state
  const [subEmail, setSubEmail] = useState('');
  const [subStatus, setSubStatus] = useState('idle'); // idle | loading | success | error
  const [subMessage, setSubMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subEmail || !/^\S+@\S+\.\S+$/.test(subEmail)) {
      setSubStatus('error');
      setSubMessage('Please enter a valid email address.');
      return;
    }
    setSubStatus('loading');
    setSubMessage('');
    try {
      const res = await fetch(`${API}/notify-me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: subEmail, product: 'episode_alerts' }),
      });
      if (!res.ok) throw new Error('failed');
      setSubStatus('success');
      setSubMessage("You're on the list! We'll let you know when new episodes drop.");
      setSubEmail('');
    } catch (err) {
      setSubStatus('error');
      setSubMessage("Something went wrong. Please try again in a bit.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/youtube/latest?limit=6`);
        if (!res.ok) throw new Error('bad response');
        const data = await res.json();
        if (!cancelled) setYtVideos(data);
      } catch (e) {
        if (!cancelled) setYtError(true);
      } finally {
        if (!cancelled) setYtLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const achievements = [
    {
      title: '#1 on Goodpods Top 100 Identity Chart',
      organization: 'Goodpods',
      description: 'Ranked #1 in the Top 100 Identity All-Time Chart, celebrating our authentic conversations about Latina identity and culture.',
      link: 'https://goodpods.com/podcasts/dos-bold-latinas-530351',
      badge: '#1 Ranked',
    },
  ];

  const features = [
    {
      title: 'Voyage Michigan Magazine',
      publication: 'Voyage Michigan',
      description: 'In-depth interview with Jatnna Abreu and Rosibel Madera Vialet about their journey, the podcast, and their mission to empower Latina women.',
      link: 'https://voyagemichigan.com/interview/check-out-dos-bold-latinas/',
      image: 'https://customer-assets.emergentagent.com/job_af4289ab-e836-496b-954f-6cfb1ffaac19/artifacts/m8gka077_d365c705-6d67-49e7-9088-4fc5792c7d5f.jpeg',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section 
        className="py-20 px-6 text-white relative overflow-hidden"
        style={{ backgroundColor: '#b76e79' }}
      >
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Media & Events
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Read what others are saying about Dos Bold Latinas, see our media coverage, 
            and check out photos from our community events!
          </p>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-2">Awards & Recognition</h2>
            <p className="text-gray-600">Celebrating our achievements and milestones</p>
          </div>

          {achievements.map((achievement, index) => (
            <Card 
              key={index} 
              className="mb-12 border-none shadow-2xl hover:shadow-3xl transition-all duration-300"
            >
              <CardContent className="p-12 text-center">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div 
                    className="w-20 h-20 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#b76e79' }}
                  >
                    <Award size={40} className="text-white" />
                  </div>
                  <span className="px-6 py-2 rounded-full text-lg font-bold text-white" style={{ backgroundColor: '#b76e79' }}>
                    {achievement.badge}
                  </span>
                </div>
                <h3 className="text-4xl font-bold mb-4">{achievement.title}</h3>
                <p className="text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
                  {achievement.description}
                </p>
                <a 
                  href={achievement.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button 
                    size="lg"
                    className="text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#b76e79' }}
                  >
                    View on Goodpods
                    <ExternalLink className="ml-2" size={20} />
                  </Button>
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-2">Featured In</h2>
            <p className="text-gray-600">Check out our latest press coverage and media appearances</p>
          </div>

          {features.map((feature, index) => (
            <Card 
              key={index} 
              className="mb-8 border-none shadow-2xl overflow-hidden hover:shadow-3xl transition-all duration-300"
            >
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-auto bg-gray-100">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-contain"
                  />
                </div>
                <CardContent className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <Award size={24} style={{ color: '#b76e79' }} />
                    <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: '#b76e79' }}>
                      {feature.publication}
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold mb-4">{feature.title}</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">
                    {feature.description}
                  </p>
                  <a 
                    href={feature.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button 
                      size="lg"
                      className="transition-all duration-300 hover:scale-105"
                      style={{ backgroundColor: '#b76e79' }}
                    >
                      Read Full Article
                      <ExternalLink className="ml-2" size={20} />
                    </Button>
                  </a>
                </CardContent>
              </div>
            </Card>
          ))}

          {/* New Podcast Appearance - YouTube Episode */}
          <Card
            className="mb-8 border-none shadow-2xl overflow-hidden hover:shadow-3xl transition-all duration-300"
            data-testid="youtube-episode-card"
          >
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative bg-black aspect-video md:aspect-auto md:min-h-[320px]">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/qRBZxtarz9k"
                  title="Dos Bold Latinas Podcast Appearance"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  data-testid="youtube-episode-iframe"
                />
              </div>
              <CardContent className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <Award size={24} style={{ color: '#b76e79' }} />
                  <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: '#b76e79' }}>
                    Podcast Appearance
                  </span>
                </div>
                <h3 className="text-3xl font-bold mb-4">Featured Guest Episode</h3>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Tune in to our latest guest appearance where we share our journey, bold conversations,
                  and what it means to show up authentically as Latina women.
                </p>
                <a
                  href="https://youtu.be/qRBZxtarz9k?si=ZkiVCJlqYN7422U5"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="lg"
                    className="transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#b76e79' }}
                    data-testid="watch-youtube-episode-btn"
                  >
                    Watch on YouTube
                    <ExternalLink className="ml-2" size={20} />
                  </Button>
                </a>
              </CardContent>
            </div>
          </Card>

          {/* Latest Episodes from our YouTube */}
          <div className="mt-16 mb-12">
            <h2 className="text-3xl font-bold mb-2">Watch Our Latest Episodes</h2>
            <p className="text-gray-600 mb-8">Fresh from our YouTube channel — updated automatically.</p>

            {ytLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="yt-loading">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-video bg-gray-200 rounded-lg mb-3" />
                    <div className="h-4 bg-gray-200 rounded w-5/6 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            )}

            {!ytLoading && ytError && (
              <p className="text-gray-500" data-testid="yt-error">
                Couldn't load episodes right now — please check back soon.
              </p>
            )}

            {!ytLoading && !ytError && ytVideos.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="yt-videos-grid">
                {ytVideos.map((v) => (
                  <a
                    key={v.video_id}
                    href={v.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                    data-testid={`yt-video-${v.video_id}`}
                  >
                    <Card className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 bg-white h-full">
                      <div className="relative aspect-video overflow-hidden bg-black">
                        <img
                          src={v.thumbnail}
                          alt={v.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors duration-300">
                          <div
                            className="w-14 h-14 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg"
                            style={{ backgroundColor: '#b76e79' }}
                          >
                            <Play size={24} className="text-white ml-1" fill="white" />
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-bold text-base leading-snug mb-2 line-clamp-2 group-hover:opacity-80 transition-opacity">
                          {v.title}
                        </h3>
                        {v.published && (
                          <p className="text-xs text-gray-500">
                            {new Date(v.published).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Subscribe for new episode alerts */}
          <Card className="border-none shadow-2xl overflow-hidden mb-8" data-testid="subscribe-card">
            <div className="grid md:grid-cols-5">
              <div
                className="md:col-span-2 p-10 flex flex-col justify-center text-white"
                style={{ backgroundColor: '#b76e79' }}
              >
                <Mail size={40} className="mb-4" />
                <h3 className="text-3xl font-bold mb-3 leading-tight">
                  Never miss an episode
                </h3>
                <p className="text-white/90 leading-relaxed">
                  Drop your email and we'll give you a heads-up the moment a new episode goes live.
                </p>
              </div>
              <div className="md:col-span-3 p-10 flex flex-col justify-center bg-white">
                <form
                  onSubmit={handleSubscribe}
                  className="flex flex-col sm:flex-row gap-3"
                  data-testid="subscribe-form"
                >
                  <Input
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={subEmail}
                    onChange={(e) => setSubEmail(e.target.value)}
                    disabled={subStatus === 'loading'}
                    className="flex-1 h-12 text-base"
                    data-testid="subscribe-email-input"
                    aria-label="Email address"
                  />
                  <Button
                    type="submit"
                    size="lg"
                    disabled={subStatus === 'loading'}
                    className="h-12 px-8 text-base font-semibold transition-all duration-300 hover:scale-105 disabled:opacity-70 disabled:hover:scale-100"
                    style={{ backgroundColor: '#b76e79' }}
                    data-testid="subscribe-submit-btn"
                  >
                    {subStatus === 'loading' ? 'Signing up...' : 'Notify Me'}
                  </Button>
                </form>
                {subStatus === 'success' && (
                  <div
                    className="mt-4 flex items-start gap-2 text-sm font-medium"
                    style={{ color: '#b76e79' }}
                    data-testid="subscribe-success"
                  >
                    <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" />
                    <span>{subMessage}</span>
                  </div>
                )}
                {subStatus === 'error' && (
                  <p
                    className="mt-4 text-sm font-medium text-red-600"
                    data-testid="subscribe-error"
                  >
                    {subMessage}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-3">
                  No spam, we promise — just new episode alerts and occasional community updates.
                </p>
              </div>
            </div>
          </Card>

          {/* Community Events Gallery */}
          <div className="mt-16 mb-12">
            <h2 className="text-3xl font-bold mb-2">Community Events</h2>
            <p className="text-gray-600 mb-8">Celebrating moments with our amazing community</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/ak93mabp_25d0fcdc-508f-4de6-b387-e618fae3dd95.jpeg"
                  alt="Community Event 1"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/1rxc6ac8_935cab2b-f2e3-470d-bd70-b1f43b2f3a94.jpeg"
                  alt="Community Event 2"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/qanu66sp_b0c8226e-6e6b-4992-bf05-4cba40f8ba21.jpeg"
                  alt="Community Event 3"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/2xb4r3cf_c970b25c-d82d-462d-88a1-562c61fce4a1.jpeg"
                  alt="Community Event 4"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/uymilhb5_200136d0-2712-4955-9c2e-e80107afa9a4.jpeg"
                  alt="Community Event 5"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/o5mmjv5e_4617a71b-5812-434b-a51b-e5d18c60190b.jpeg"
                  alt="Community Event 6"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/qfkbm4q2_8c4a98fb-729e-49a6-9def-02a6b861b4d9.jpeg"
                  alt="Community Event 7"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/q7mpleej_4dab5762-2ba6-474d-ab86-ef52625a8ba8.jpeg"
                  alt="Community Event 8"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/76oo47lq_3ed2a758-8e5d-4739-8628-4a53a8068576.jpeg"
                  alt="Community Event 9"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/vm94tbwh_31f7a65b-598f-4176-8bc8-1dcd354b0775.jpeg"
                  alt="Community Event 10"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/v617n3hk_8f7791b6-051d-48a3-9eac-2233ad1ee8ba.jpeg"
                  alt="Community Event 11"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="relative h-64 overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/dyjohsm7_af4a8ade-739c-4eb7-a591-f7cf36a4c82e.jpeg"
                  alt="Community Event 12"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>

          {/* YouTube Channel Promotion */}
          <Card className="border-none shadow-xl mb-8 overflow-hidden">
            <div className="relative bg-gray-100">
              <img
                src="https://customer-assets.emergentagent.com/job_sleek-latina-site/artifacts/iwmuizkx_channels4_banner.jpg"
                alt="Dos Bold Latinas YouTube"
                className="w-full h-auto object-contain"
              />
            </div>
            <CardContent className="p-8 text-center">
              <p className="text-lg text-gray-600 mb-6">
                Subscribe to our YouTube channel for video episodes, behind-the-scenes content, 
                and exclusive interviews!
              </p>
              <a 
                href="https://youtube.com/@dosboldlatinas?si=bnscS8wgBS6Hf9wd&sub_confirmation=1" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg"
                  className="text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
                  style={{ backgroundColor: '#b76e79' }}
                >
                  Subscribe on YouTube
                  <ExternalLink className="ml-2" size={20} />
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* Press Kit / Contact */}
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white border-none">
            <CardContent className="p-12 text-center">
              <Newspaper size={48} className="mx-auto mb-6" />
              <h3 className="text-3xl font-bold mb-4">
                Media Inquiries
              </h3>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Interested in featuring Dos Bold Latinas? We'd love to hear from you!
              </p>
              <a href="mailto:dosboldlatinas@gmail.com">
                <Button 
                  size="lg" 
                  className="text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
                  style={{ backgroundColor: '#b76e79' }}
                >
                  Contact Us
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Press;